import React, { useCallback, useState, useEffect, useRef, useImperativeHandle } from "react";
import styles from "./customChart.module.scss";
import { Radio, Space, DatePicker, Select } from "antd";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { merge } from "lodash";
import Service from "../../services";
import { formatReadableNumber, isArbitrage } from "../../utils";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Option } = Select;

const USDS_TYPE = "USD";

const initialSummaryList = [
  { name: "Net Profit/Loss", value: "-" },
  { name: "Total Effective Commission Rate", value: "-" },
  { name: "Total Commission (USD)", value: "-" },
  { name: "Total Volume (USD)", value: "-" },
  { name: "Maker Commission Rate", value: "-" },
  { name: "Effective Maker Commission Rate", value: "-" },
  { name: "Maker Commission", value: "-" },
  { name: "Maker Ratio", value: "-" },
  { name: "Maker Volume", value: "-" },
  { name: "Taker Commission Rate", value: "-" },
  { name: "Effective Taker Commission Rate", value: "-" },
  { name: "Taker Commission", value: "-" },
  { name: "Taker Ratio", value: "-" },
  { name: "Taker Volume", value: "-" },
];

const initChart = {
  loading: {
    enabled: true,
    hideDuration: 1000,
    showDuration: 1000,
  },
  chart: {
    // zoomType: "xy",
    spacing: [10, 50, 10, 10],
  },
  title: {
    text: "12h/bar",
    align: "left",
    x: 30,
  },
  yAxis: {
    minPadding: 0,
    maxPadding: 0,
    labels: {
      style: {
        color: "rgba(24, 144, 255, 0.85)",
      },
    },
  },
  tooltip: {
    shared: true,
  },
  legend: {
    enabled: false,
  },
  scrollbar: {
    enabled: true,
  },
  series: [
    {
      name: "",
      type: "column",
      pointWidth: 30,
      color: "rgba(24, 144, 255, 0.85)",
    },
  ],
};

const CHART_TYPE = {
  accountBalance: "accountBalance",
  accountPnl: "accountPnl",
  sessionPnl: "sessionPnl",
};

const CustomChart = ({ setMainLoading, accountList, accountIndex, customChartRef, setBalance, tradingPair }) => {
  const highChart = useRef();

  const [chartType, setChartType] = useState(CHART_TYPE.accountBalance);
  const [currencyType, setCurrencyType] = useState(); //货币类型
  const [currencyTypeList, setCurrencyTypeList] = useState([]); //货币类型下拉列表
  const [chartOptions, setChartOptions] = useState({});
  const [balances, setBalances] = useState(); // 图标Y轴数值数据
  const [rangeInDays, setRangeInDays] = useState(7);
  const [rangePicker, setRangePicker] = useState([dayjs().subtract(rangeInDays, "days"), dayjs()]);
  const [summaryList, setSummaryList] = useState(initialSummaryList);
  const [tradeSessions, setTradeSessions] = useState([]);
  const [sessionSummaryList, setSessionSummaryList] = useState();
  const [sessionChartOption, setSessionChartOption] = useState();

  useEffect(() => {
    const tradeSession = tradeSessions.filter((session) => {
      // For arbitrage accounts, filter by the current session symbol when in sessionPnl view
      if (isArbitrage(accountList[accountIndex]?.exchange)) {
        if (chartType === CHART_TYPE.sessionPnl && tradingPair) {
          return session.symbol.includes(tradingPair);
        }
        return true; // Show all for other chart types
      }

      if (currencyType === USDS_TYPE || currencyType === "BNB") {
        return session.symbol.includes(tradingPair); // usds future
      } else {
        return session.symbol.includes(currencyType); // coin future
      }
    });
    setSessionSummaryList(getSessionSummaryList(tradeSession));
    setSessionChartOption(getSessionChartOption(tradeSession));
  }, [currencyType, tradeSessions, tradingPair, accountList, accountIndex, chartType]);

  useImperativeHandle(customChartRef, () => ({
    // changeVal 就是暴露给父组件的方法
    fetchAccountBalances: () => fetchAccountBalances(),
    setCurrencyTypeList: (obj) => setCurrencyTypeList(obj),
  }));

  //获取交易明细 (默认7天前)
  const fetchAccountBalances = useCallback(async () => {
    highChart.current.chart.showLoading();
    setMainLoading(true);

    const { aggregBalances, parentBalances, tradeSessionsArray } = await Service.getAndParseAccountBalances({
      exchangeInstrument: accountList[accountIndex].exchange,
      account: accountList[accountIndex].account,
      startAt: rangePicker[0].valueOf(),
      endAt: rangePicker[1].valueOf(),
    });
    setTradeSessions(tradeSessionsArray);
    let balanceDataSource = [];
    balanceDataSource = Object.values({ ...aggregBalances, ...parentBalances });
    balanceDataSource = balanceDataSource.sort((a, b) => {
      if (!a.parentAccount) return -1; // parentAccount will be at the bottom
      if (!b.parentAccount) return 1;
      return a.key.localeCompare(b.key);
    });

    //注入选择列表
    if (balanceDataSource[0] && balanceDataSource[0].balancesObj) {
      let currencyList = Object.keys(Object.values(balanceDataSource[0].balancesObj).reverse()[0]);
      // 账户中有usdt,busd,usdc，将这几个currency合并为一个currencyType
      if (currencyList.find((currency) => currency.includes("USD"))) {
        const totals = ["totalInBTC", "totalInUSDT"].filter((total) => currencyList.includes(total));
        currencyList = [...currencyList.filter((c) => !c.includes("USD") && !c.includes("total")), ...totals, USDS_TYPE];
      }
      setCurrencyTypeList(currencyList);
      if (!currencyTypeList.length && accountList[accountIndex]) {
        currencyList.sort((a, b) => a.localeCompare(b));
        const usdIndex = currencyList.findIndex((currency) => currency.includes("USD"));
        // Find the index of currency that matches the symbolShort of the account
        const accountSymbolShort = accountList[accountIndex].symbolShort;
        const defaultCurrencyIndex = currencyList.findIndex((currency) => currency === accountSymbolShort);
        let defaultCurrency;
        if (usdIndex !== -1) {
          defaultCurrency = currencyList[usdIndex];
        } else if (defaultCurrencyIndex !== -1) {
          defaultCurrency = currencyList[defaultCurrencyIndex];
        } else {
          defaultCurrency = currencyList[0];
        }
        setCurrencyType(defaultCurrency);
      }
    } else {
      setChartOptions({
        series: { data: [] },
      });
      setSummaryList(initialSummaryList);
    }

    setMainLoading(false);
    highChart.current.chart.hideLoading();

    setBalances(balanceDataSource);
    setBalance(balanceDataSource);
  }, [accountIndex, accountList, currencyTypeList.length, rangePicker, setBalance, setMainLoading]);

  const updateSummaryAndChart = useCallback(() => {
    const balanceIsReady = balances && balances[0] && balances[0].balancesObj;
    if (!balanceIsReady) return;

    const DEFAULT_CURRENCY_BALANCE = 0;
    const _selectedCurrencyBalances = Object.keys(balances[0].balancesObj)
      // .filter((e) => e[currencyType || currencyTypeList[0]])
      .map((dateTime) => {
        const balance = balances[0].balancesObj[dateTime];
        if (currencyType === USDS_TYPE) {
          // 在pnl曲线中合并BUSD和USDT的balance
          const balanceUSDT = balance["USDT"] ? balance["USDT"] : 0;
          const balanceBUSD = balance["BUSD"] ? balance["BUSD"] : 0;
          const balanceUSDC = balance["USDC"] ? balance["USDC"] : 0;
          return balanceUSDT + balanceBUSD + balanceUSDC;
        }
        return balance[currencyType || currencyTypeList[0]] || DEFAULT_CURRENCY_BALANCE;
      });

    const _balanceDiffList = [];
    for (let i = 0; i < _selectedCurrencyBalances.length - 1; i++) {
      _balanceDiffList.push(_selectedCurrencyBalances[i + 1] - _selectedCurrencyBalances[i]);
    }

    const timestamps = Object.keys(balances[0].balancesObj)
      .splice(1)
      .filter((time) => Object.keys(balances[0].balancesObj[time]).length > 0);

    const chartWidth = highChart.current.container.current.offsetWidth;
    const offsetWidth = chartWidth / (_balanceDiffList.length * 2); //每个格占满的宽度
    if (chartType === CHART_TYPE.sessionPnl) {
      setChartOptions(sessionChartOption);
    } else {
      setChartOptions({
        title: {
          text: "",
        },
        xAxis: [
          {
            categories: timestamps,
          },
        ],
        series: [
          {
            type: chartType === CHART_TYPE.accountPnl ? "column" : "spline",
            color: "rgb(24, 144, 255)",
            pointWidth: offsetWidth > 30 ? 30 : offsetWidth < 10 ? 10 : offsetWidth, //10~30 之间浮动
            data: chartType === CHART_TYPE.accountPnl ? _balanceDiffList : _selectedCurrencyBalances,
            showInLegend: false,
          },
        ],
      });
    }

    //计算Summary
    const totalProfit = _balanceDiffList
      .filter((e) => e > 0)
      .reduce((a, b) => a + b, 0)
      .toFixed(2);
    const totalLoss = _balanceDiffList
      .filter((e) => e < 0)
      .reduce((a, b) => a + b, 0)
      .toFixed(2);
    const netProfit = formatReadableNumber(parseFloat(totalProfit) + parseFloat(totalLoss));
    //计算最长正收益天数
    const longestWinStrike = [];
    let count = 0;
    _balanceDiffList.forEach((e) => {
      if (e > 0) {
        count++;
      } else {
        longestWinStrike.push(count);
        count = 0;
      }
    });
    longestWinStrike.push(count);

    const currencyUnit = currencyType && currencyType.includes("USD") ? "$" : "";

    if (chartType === CHART_TYPE.sessionPnl) {
      setSummaryList(sessionSummaryList);
    } else if (isArbitrage(accountList[accountIndex]?.exchange) && tradeSessions.length > 0) {
      // arbitrage account summary - use session PnL instead of balance diff
      const accountSummaryList = getSessionSummaryList(tradeSessions);
      setSummaryList(accountSummaryList);
    } else if (currencyType && currencyType.includes("USD") && tradeSessions.find(({ symbol }) => symbol === "ALL")) {
      // usds account summary
      const accountSummary = tradeSessions.filter(({ symbol }) => symbol === "ALL");
      const accountSummaryList = getSessionSummaryList(accountSummary);
      accountSummaryList.unshift({
        name: "Balance Change",
        value: netProfit,
      });
      setSummaryList(accountSummaryList);
    } else if (currencyType && tradeSessions.find(({ symbol }) => symbol.includes(currencyType))) {
      // coin-m account summary
      const accountSummary = tradeSessions.filter(({ symbol }) => symbol.includes(currencyType));
      const accountSummaryList = getSessionSummaryList(accountSummary);
      accountSummaryList.unshift({
        name: "Balance Change",
        value: netProfit,
      });
      setSummaryList(accountSummaryList);
    } else {
      setSummaryList([
        {
          name: "Net Profit/Loss",
          value: currencyUnit + netProfit,
        },
        {
          name: "Total Profit",
          value: currencyUnit + totalProfit,
        },
        {
          name: "Total Loss",
          value: currencyUnit + totalLoss,
        },
        {
          name: "Win Rate",
          value: ((_balanceDiffList.filter((e) => e > 0).length / _balanceDiffList.filter((e) => e !== 0).length) * 100).toFixed(2) + "%",
        },
        {
          name: "Average Profit/Loss",
          value: currencyUnit + ((netProfit / _balanceDiffList.length) * 2).toFixed(2), // 每个balance 12 小时，因此要乘2来计算daily average
        },
        {
          name: "Longest Win Strike",
          value: Math.max(...longestWinStrike) || 0,
        },
      ]);
    }
  }, [
    balances,
    chartType,
    currencyType,
    currencyTypeList,
    sessionChartOption,
    sessionSummaryList,
    tradeSessions,
    accountList,
    accountIndex,
  ]);

  useEffect(() => {
    setChartOptions(merge({}, initChart, getPercentDifChartOptions()));
  }, []);

  useEffect(() => {
    balances && updateSummaryAndChart();
  }, [balances, updateSummaryAndChart]);

  useEffect(() => {
    if (accountIndex >= 0) {
      fetchAccountBalances();
    }
  }, [accountIndex, fetchAccountBalances, rangePicker]);

  function getSessionChartOption(tradeSession) {
    const pnlData = tradeSession.map((dataPoint) => ({
      // data point has the data over a period of time. e.g. pnl for last hour
      date: dataPoint.timestamp,
      pnl: dataPoint.tradeMetrics.pnl,
    }));
    const accumulatedPnL = pnlData.reduce((acc, current) => {
      const prevPnL = acc.length > 0 ? acc[acc.length - 1] : 0;
      const pnl = Number((prevPnL + current.pnl).toFixed(2));
      return [...acc, pnl];
    }, []);
    const chartWidth = highChart?.current?.container?.current?.offsetWidth;
    const offsetWidth = chartWidth / (accumulatedPnL.length * 2); //每个格占满的宽度
    const options = {
      title: {
        text: "",
        enabled: false,
      },
      xAxis: {
        categories: pnlData.map((data) => data.date),
      },
      series: [
        {
          type: "spline",
          color: "rgb(24, 144, 255)",
          pointWidth: offsetWidth > 30 ? 30 : offsetWidth < 10 ? 10 : offsetWidth, //10~30 之间浮动
          data: accumulatedPnL,
          showInLegend: false,
        },
      ],
    };
    return options;
  }
  return (
    <div className={styles.detailChart}>
      <div className={styles.chartHeader}>
        <Radio.Group value={chartType} onChange={(e) => setChartType(e.target.value)}>
          <Radio.Button value={CHART_TYPE.accountBalance}>Account Balance</Radio.Button>
          <Radio.Button value={CHART_TYPE.accountPnl}>Account PNL</Radio.Button>
          <Radio.Button value={CHART_TYPE.sessionPnl}>Session Accumulated PNL</Radio.Button>
        </Radio.Group>
        <Space>
          <Radio.Group
            value={rangeInDays}
            onChange={(e) => {
              setRangeInDays(e.target.value);
              setRangePicker([dayjs().subtract(e.target.value, "days"), dayjs()]);
            }}
          >
            <Radio.Button value={7}>7-days</Radio.Button>
            <Radio.Button value={30}>30-days</Radio.Button>
            <Radio.Button value={90}>90-days</Radio.Button>
          </Radio.Group>
          <RangePicker
            showTime
            value={rangePicker}
            onChange={(e) => {
              setRangeInDays(undefined); // unselect rangeInDays buttonGroup
              setRangePicker(e);
            }}
          />
          <Select style={{ width: 200 }} value={currencyType} onChange={(e) => setCurrencyType(e)}>
            {currencyTypeList.map((e) => (
              <Option key={e}>{e}</Option>
            ))}
          </Select>
        </Space>
      </div>
      <div className={styles.chartContent}>
        <div className={styles.chart}>
          <HighchartsReact
            ref={highChart}
            style={{ width: "70%" }}
            highcharts={Highcharts}
            options={chartOptions}
            containerProps={{ style: { height: "100%" } }}
          />
        </div>
        <div className={styles.summaryTap}>
          <div className={styles.tapTitle}>Summary</div>
          <div className={styles.tapList}>
            {summaryList.map((e, i) => (
              <div key={e.name} className={styles.tapLi}>
                <div className={styles.tapTag}>{i + 1}</div>
                <div className={styles.tapText}>{e.name}</div>
                <div className={styles.tapPrice}>{e.value}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

function getSessionSummaryList(tradeSession) {
  const aggregatedMetrics = tradeSession.reduce(
    (accumulator, dataPoint) => {
      // data point has the data over a period of time. e.g. pnl for last hour
      accumulator.pnl += dataPoint.tradeMetrics.pnl;
      accumulator.makerVolume += dataPoint.tradeMetrics.makerVolume;
      accumulator.makerCommission += dataPoint.tradeMetrics.makerCommission;
      accumulator.makerCommissionRate = dataPoint.tradeMetrics.makerCommissionRate; // display most recent rate
      accumulator.takerVolume += dataPoint.tradeMetrics.takerVolume;
      accumulator.takerCommission += dataPoint.tradeMetrics.takerCommission;
      accumulator.takerCommissionRate = dataPoint.tradeMetrics.takerCommissionRate; // display most recent rate
      return accumulator;
    },
    {
      pnl: 0,
      makerVolume: 0,
      makerCommission: 0,
      makerCommissionRate: 0,
      takerVolume: 0,
      takerCommission: 0,
      takerCommissionRate: 0,
    }
  );
  const { pnl, makerVolume, makerCommission, takerVolume, takerCommission, makerCommissionRate, takerCommissionRate } = aggregatedMetrics;

  const totalVolume = takerVolume + makerVolume;
  const totalCommission = makerCommission + takerCommission;
  const totalCommissionRate = totalVolume === 0 ? 0 : totalCommission / totalVolume;
  const makerRatio = totalVolume === 0 ? 0 : makerVolume / totalVolume;
  const takerRatio = totalVolume === 0 ? 0 : takerVolume / totalVolume;
  const effectiveMakerCommissionRate = makerVolume === 0 ? 0 : makerCommission / makerVolume;
  const effectiveTakerCommissionRate = takerVolume === 0 ? 0 : takerCommission / takerVolume;

  const list = [
    {
      name: "Net Profit/Loss",
      value: formatReadableNumber(pnl),
    },
    {
      name: "Total Effective Commission Rate",
      value: (totalCommissionRate * 100).toFixed(4) + "%",
    },
    {
      name: "Total Commission (USD)",
      value: formatReadableNumber(totalCommission),
    },
    {
      name: "Total Volume (USD)",
      value: formatReadableNumber(totalVolume),
    },
    { name: "Maker Commission Rate", value: (makerCommissionRate * 100).toFixed(4) + "%" },
    {
      name: "Effective Maker Commission Rate",
      value: (effectiveMakerCommissionRate * 100).toFixed(4) + "%",
    },
    {
      name: "Maker Commission (USD)",
      value: formatReadableNumber(makerCommission),
    },
    { name: "Maker Ratio", value: (makerRatio * 100).toFixed(2) + "%" },
    {
      name: "Maker Volume (USD)",
      value: formatReadableNumber(makerVolume),
    },
    { name: "Taker Commission Rate", value: (takerCommissionRate * 100).toFixed(4) + "%" },
    {
      name: "Effective Taker Commission Rate",
      value: (effectiveTakerCommissionRate * 100).toFixed(4) + "%",
    },
    {
      name: "Taker Commission (USD)",
      value: formatReadableNumber(takerCommission),
    },
    { name: "Taker Ratio", value: (takerRatio * 100).toFixed(2) + "%" },
    {
      name: "Taker Volume (USD)",
      value: formatReadableNumber(takerVolume),
    },
  ];
  return list;
}

// use this chart option on the initialization of the chart to show percent dif
function getPercentDifChartOptions() {
  // add custom event because mousedown and mouseup is not available in hightchart point
  let isMousedownEventAdded = false;
  let isMouseupEventAdded = false;
  let isLabelGroupAdded = false;

  return {
    plotOptions: {
      series: {
        marker: {
          radius: 4,
        },
        point: {
          events: {
            mouseOver() {
              const point = this;
              const chart = point.series.chart;

              // only add event once
              if (!isLabelGroupAdded) {
                chart.labelGroup = chart.renderer.g("labelGroup").add();
                isLabelGroupAdded = true;
              }
              // only add event once
              if (!isMousedownEventAdded) {
                const container = point.series.chart.container;
                container.addEventListener("mousedown", () => {
                  chart.mouseDown = true;
                  chart.pointMouseDown = chart.hoverPoint;
                });
                isMousedownEventAdded = true;
              }
              // only add event once
              if (!isMouseupEventAdded) {
                const container = point.series.chart.container;
                container.addEventListener("mouseup", () => {
                  chart.mouseDown = false;
                  chart.pointMouseDown = undefined;
                  if (chart.label) {
                    chart.label.destroy();
                    chart.label = undefined;
                  }
                  if (chart.selectionRect) {
                    chart.selectionRect.destroy();
                    chart.selectionRect = undefined;
                  }
                });
                isMouseupEventAdded = true;
              }

              // calculate and draw profit only on mousedown
              if (chart.mouseDown) {
                if (!chart.pointMouseDown) {
                  chart.pointMouseDown = point;
                }
                if (chart.label) {
                  chart.label.destroy();
                  chart.label = undefined;
                }
                if (chart.selectionRect) {
                  chart.selectionRect.destroy();
                  chart.selectionRect = undefined;
                }
                const dif = point.y - chart.pointMouseDown.y;
                const percentDif = Math.round((dif / chart.pointMouseDown.y) * 10000) / 100;
                const displayText = `${formatReadableNumber(dif)} (${percentDif}%)`; // text format: 123,456.78 (11.12%)
                const rectWidth = point.plotX - chart.pointMouseDown.plotX;

                let x, sign;
                if (rectWidth > 0) {
                  x = chart.plotLeft + chart.pointMouseDown.plotX;
                  sign = 1;
                } else {
                  x = chart.plotLeft + point.plotX;
                  sign = -1;
                }

                chart.selectionRect = chart.renderer
                  .rect(
                    x, // x
                    chart.plotTop, // y
                    (point.plotX - chart.pointMouseDown.plotX) * sign, // width
                    chart.plotSizeY // height
                  )
                  .attr({
                    fill: "#e0f5ff",
                    zIndex: -1,
                  })
                  .add();
                chart.label = chart.renderer
                  .label(dif > 0 ? "+" + displayText : displayText, x + (point.plotX - chart.pointMouseDown.plotX) * sign * 0.5, 60)
                  .add(chart.labelGroup);
                chart.labelGroup.translate(-chart.label.width / 2, 0).toFront();
              }
            },
          },
        },
      },
    },
  };
}

export default CustomChart;
